import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Building2, CheckCircle, ArrowLeft, Mail, Phone, User, Briefcase, Crown, Check,
  Plus, Search, MapPin, Coffee, Beer, BookOpen, BriefcaseBusiness, Building, MoreHorizontal,
  Wifi, Plug, Volume2, Car, UtensilsCrossed, GlassWater, Users, Locate, Gift, X,
  BarChart3, Palette, Calendar, Image, Globe, Star, Shield, Sparkles
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { getVenueImage } from "../utils/defaultImages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const venueTypes = [
  { value: "cafe", label: "Cafe", icon: Coffee },
  { value: "pub", label: "Pub", icon: Beer },
  { value: "hotel", label: "Hotel", icon: Building },
  { value: "library", label: "Library", icon: BookOpen },
  { value: "hub", label: "Coworking Hub", icon: BriefcaseBusiness },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

const wifiOptions = [
  { value: "very_fast", label: "Very Fast" },
  { value: "fast", label: "Fast" },
  { value: "good", label: "Good" },
  { value: "poor", label: "Poor" },
  { value: "very_poor", label: "Very Poor" },
  { value: "none", label: "None" },
];

const noiseOptions = [
  { value: "silent", label: "Silent" },
  { value: "quiet", label: "Quiet" },
  { value: "moderate", label: "Moderate" },
  { value: "lively", label: "Lively" },
  { value: "loud", label: "Loud" },
];

export default function ClaimVenuePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get("venue");
  const promoCode = searchParams.get("promo");
  
  const [mode, setMode] = useState("claim"); // "claim" or "add"
  const [step, setStep] = useState(venueId ? 2 : 1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [ownerId, setOwnerId] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(promoCode || "");
  const [promoValidated, setPromoValidated] = useState(false);
  const [promoDetails, setPromoDetails] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  
  // Owner details form
  const [formData, setFormData] = useState({
    owner_name: "", owner_email: "", owner_phone: "", company_name: "", message: ""
  });

  // New venue form (for "Add Your Venue" mode)
  const [venueData, setVenueData] = useState({
    name: "",
    venue_type: "cafe",
    description: "",
    address: "",
    city: "",
    postcode: "",
    latitude: null,
    longitude: null,
    wifi_quality: "good",
    noise_level: "moderate",
    power_outlets: 5,
    has_meeting_rooms: false,
    has_parking: false,
    has_food: false,
    has_drinks: true,
    coffee_avg: 3.50,
    opening_hours: ""
  });

  // Scroll to top on mount (important for mobile QR scanning)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Fetch pricing
    axios.get(`${API}/pricing`).then(res => setPricing(res.data)).catch(() => {});
    
    if (venueId) {
      axios.get(`${API}/venues/${venueId}`).then(res => setSelectedVenue(res.data)).catch(() => {
        toast.error("Venue not found");
        navigate('/claim');
      });
    }
    
    // Validate promo code if provided in URL
    if (promoCode) {
      axios.post(`${API}/vouchers/validate`, { code: promoCode })
        .then(res => {
          setPromoValidated(true);
          setPromoDetails(res.data);
          // Show premium features modal for promo users
          setShowPremiumModal(true);
        })
        .catch(() => {
          setAppliedPromo("");
          toast.error("Invalid promo code");
        });
    }
  }, [venueId, navigate, promoCode]);

  const searchVenues = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const response = await axios.get(`${API}/venues`, { params: { search: searchQuery, limit: 10 } });
      setSearchResults(response.data.filter(v => !v.is_claimed));
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setVenueData(prev => ({ ...prev, latitude, longitude }));
        
        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          if (data.address) {
            setVenueData(prev => ({
              ...prev,
              address: data.address.road || data.address.pedestrian || "",
              city: data.address.city || data.address.town || data.address.village || "",
              postcode: data.address.postcode || ""
            }));
            toast.success("Location found!");
          }
        } catch (error) {
          console.error("Geocoding error:", error);
        }
        setGettingLocation(false);
      },
      (error) => {
        toast.error("Could not get location");
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!formData.owner_name || !formData.owner_email) {
      toast.error("Name and email required");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/claims`, {
        venue_id: selectedVenue?.id || venueId,
        promo_code: appliedPromo || undefined,
        ...formData
      });
      setOwnerId(response.data.owner_id);
      setClaimed(true);
      
      // If promo code was applied, try to redeem it
      if (appliedPromo && promoValidated && response.data.owner_id) {
        try {
          await axios.post(`${API}/vouchers/redeem`, {
            code: appliedPromo,
            owner_id: response.data.owner_id,
            venue_id: selectedVenue?.id || venueId
          });
          toast.success("Venue claimed with promo code applied!");
        } catch (promoErr) {
          toast.success("Venue claimed!");
          console.error("Failed to redeem promo:", promoErr);
        }
      } else {
        toast.success("Venue claimed!");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to claim");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddVenueSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.owner_name || !formData.owner_email) {
      toast.error("Your name and email are required");
      return;
    }
    if (!venueData.name || !venueData.city) {
      toast.error("Venue name and city are required");
      return;
    }

    setSubmitting(true);
    try {
      // Create the venue and claim it in one request
      const response = await axios.post(`${API}/owner/add-venue`, {
        owner: {
          owner_name: formData.owner_name,
          owner_email: formData.owner_email,
          owner_phone: formData.owner_phone,
          company_name: formData.company_name
        },
        venue: {
          name: venueData.name,
          venue_type: venueData.venue_type,
          description: venueData.description || `A great ${venueData.venue_type} for remote work.`,
          location: {
            address: venueData.address,
            city: venueData.city,
            postcode: venueData.postcode,
            lat: venueData.latitude || 51.5074,
            lng: venueData.longitude || -0.1278
          },
          amenities: {
            wifi_quality: venueData.wifi_quality,
            noise_level: venueData.noise_level,
            power_outlets: parseInt(venueData.power_outlets) || 5,
            has_meeting_rooms: venueData.has_meeting_rooms,
            has_parking: venueData.has_parking,
            has_food: venueData.has_food,
            has_drinks: venueData.has_drinks
          },
          price_range: {
            coffee_avg: parseFloat(venueData.coffee_avg) || 3.50
          },
          opening_hours: venueData.opening_hours
        },
        promo_code: appliedPromo && promoValidated ? appliedPromo : undefined
      });
      
      setOwnerId(response.data.owner_id);
      setClaimed(true);
      
      // If promo code was applied and we have a submission_id, voucher will be redeemed after approval
      // For now, store the promo code with the submission
      if (appliedPromo && promoValidated) {
        toast.success("Venue submitted! Your promo code will be applied after approval.");
      } else {
        toast.success("Venue submitted for review!");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to add venue");
    } finally {
      setSubmitting(false);
    }
  };

  if (claimed) {
    const isPremiumGift = promoValidated && promoDetails?.voucher_type === 'premium_trial';
    
    return (
      <div className={`min-h-screen ${isPremiumGift ? 'bg-gradient-to-b from-amber-50 via-orange-50 to-yellow-50' : 'bg-surface'}`}>
        <Header />
        <div className="pt-20 px-4 max-w-md mx-auto text-center">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            className={`rounded-xl p-6 ${isPremiumGift ? 'bg-white shadow-xl border-2 border-amber-200' : 'bg-white border border-gray-100'}`}
          >
            {isPremiumGift ? (
              <>
                {/* Premium Gift Success */}
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg shadow-orange-500/30"
                >
                  <Crown className="w-10 h-10 text-white" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-bold mb-3">
                    <CheckCircle className="w-4 h-4" />
                    PREMIUM ACTIVATED!
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    🎉 Your Free Month is Live!
                  </h1>
                  <p className="text-gray-600 mb-4">
                    Congratulations! Your venue now has <strong className="text-orange-600">Premium features</strong> for the next 30 days - completely free!
                  </p>
                  
                  {/* What's Active */}
                  <div className="bg-amber-50 rounded-xl p-4 mb-5 text-left">
                    <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Now Active:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-amber-800">
                      <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600" /> Featured placement</div>
                      <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600" /> Up to 5 photos</div>
                      <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600" /> Contact display</div>
                      <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600" /> Analytics</div>
                      <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600" /> Custom branding</div>
                      <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-600" /> Booking system</div>
                    </div>
                  </div>
                </motion.div>
              </>
            ) : (
              <>
                {/* Standard Success */}
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-xl font-semibold text-gray-900 mb-2">
                  {mode === "add" ? "Venue Added!" : "Venue Claimed!"}
                </h1>
                <p className="text-sm text-gray-600 mb-5">Go to your dashboard to customize your listing and add photos.</p>
              </>
            )}
            
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/dashboard?owner=${ownerId}`);
                }} 
                className={`py-3 px-4 rounded-xl font-semibold text-white transition-all ${isPremiumGift ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg' : 'btn-primary'}`}
                data-testid="go-to-dashboard"
              >
                {isPremiumGift ? '🚀 Go to Premium Dashboard' : 'Go to Dashboard'}
              </button>
              <button 
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate('/');
                }} 
                className="btn-secondary"
              >
                Back to Home
              </button>
            </div>
            
            {isPremiumGift && (
              <p className="text-xs text-gray-500 mt-4">
                Your trial ends in 30 days. No card required. Cancel anytime.
              </p>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <SEO 
        title="Claim Your Venue - List Your Business on Strider"
        description="Own a cafe, pub, hotel, library or coworking space? Claim your venue listing on Strider. Get featured to remote workers looking for laptop-friendly workspaces with WiFi and power outlets."
        keywords="claim venue, list business Strider, venue owner, add my cafe, list coworking space, register venue, business listing UK"
        canonical="/claim"
      />
      <Header />
      
      {/* Premium Features Lightbox Modal */}
      <AnimatePresence>
        {showPremiumModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPremiumModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 p-6 rounded-t-2xl">
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Premium Features Unlocked!</h2>
                    <p className="text-white/80 text-sm">Your promo code includes 30 days free</p>
                  </div>
                </div>
                <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Sparkles className="w-4 h-4 text-white" />
                  <span className="text-white font-semibold">{appliedPromo}</span>
                  <span className="text-white/80 text-sm">applied</span>
                </div>
              </div>
              
              {/* Features Grid */}
              <div className="p-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">What You Get</h3>
                
                <div className="space-y-4">
                  {[
                    { icon: Star, title: "Featured Placement", desc: "Appear at the top of search results and get 3x more visibility", color: "text-amber-500", bg: "bg-amber-50" },
                    { icon: Image, title: "Up to 5 Photos", desc: "Showcase your venue with a full gallery instead of just 2", color: "text-blue-500", bg: "bg-blue-50" },
                    { icon: Phone, title: "Contact Details Displayed", desc: "Show your phone, email, and social media links directly on your listing", color: "text-green-500", bg: "bg-green-50" },
                    { icon: Calendar, title: "Booking System", desc: "Let customers book workspace slots directly through your listing", color: "text-purple-500", bg: "bg-purple-50" },
                    { icon: BarChart3, title: "Analytics Dashboard", desc: "Track views, favorites, and see how customers find you", color: "text-cyan-500", bg: "bg-cyan-50" },
                    { icon: Palette, title: "Custom Branding", desc: "Stand out with your own brand colors on your venue page", color: "text-pink-500", bg: "bg-pink-50" },
                    { icon: Globe, title: "Website Link", desc: "Direct link to your website displayed prominently", color: "text-indigo-500", bg: "bg-indigo-50" },
                    { icon: Shield, title: "Verified Badge Eligible", desc: "Get the trusted 'Verified Workspace' badge after verification", color: "text-teal-500", bg: "bg-teal-50" },
                  ].map((feature) => (
                    <div key={feature.title} className="flex items-start gap-3">
                      <div className={`w-10 h-10 ${feature.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <feature.icon className={`w-5 h-5 ${feature.color}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{feature.title}</h4>
                        <p className="text-sm text-gray-500">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pricing Reminder */}
                <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-green-800">🎉 One FREE Month - No Commitment!</p>
                      <p className="text-sm text-green-700">No credit card required to start</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-green-600">Then £149/year or £15/month</p>
                      <p className="text-xs text-green-600">Your listing stays free if you don't upgrade</p>
                    </div>
                  </div>
                </div>
                
                {/* CTA */}
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="w-full mt-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/25"
                >
                  Claim Your Venue Now
                </button>
                <p className="text-center text-xs text-gray-400 mt-3">
                  Your 30-day free trial starts when you claim your venue
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="pt-20 px-4 pb-12 max-w-lg mx-auto relative">
        {/* Persistent FREE MONTH Banner for promo users */}
        {promoValidated && promoDetails?.voucher_type === 'premium_trial' && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed top-16 left-0 right-0 z-40 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 py-2 px-4 text-center shadow-lg"
          >
            <div className="flex items-center justify-center gap-2 text-white font-bold text-sm">
              <Gift className="w-4 h-4" />
              <span>🎉 1 MONTH FREE PREMIUM - Your gift is being applied!</span>
              <Gift className="w-4 h-4" />
            </div>
          </motion.div>
        )}
        
        {/* Strider Logo Watermark */}
        <div 
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none z-0"
          style={{
            width: '600px',
            height: '600px',
            backgroundImage: 'url(/strider-logo-bg.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        <button onClick={() => { window.scrollTo(0, 0); step === 1 ? navigate(-1) : setStep(1); }} className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand mb-4 relative z-10" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="text-center mb-6 relative z-10">
          <div className="w-12 h-12 mx-auto rounded-xl bg-brand/10 flex items-center justify-center mb-3">
            <Building2 className="w-6 h-6 text-brand" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">For Venue Owners</h1>
          <p className="text-sm text-gray-500 mt-1">Claim your listing or add a new venue</p>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
          <button
            onClick={() => { setMode("claim"); setStep(1); window.scrollTo(0, 0); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
              mode === "claim" ? "bg-white text-brand shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
            data-testid="mode-claim"
          >
            <Search className="w-4 h-4" />
            Claim Existing
          </button>
          <button
            onClick={() => { setMode("add"); setStep(1); window.scrollTo(0, 0); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
              mode === "add" ? "bg-white text-green-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
            data-testid="mode-add"
          >
            <Plus className="w-4 h-4" />
            Add Your Venue
          </button>
        </div>

        {/* CLAIM MODE */}
        {mode === "claim" && (
          <>
            {/* Steps */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className={`flex items-center gap-1.5 ${step >= 1 ? 'text-brand' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${step >= 1 ? 'bg-brand text-white' : 'bg-gray-200'}`}>1</div>
                <span className="text-xs font-medium">Find</span>
              </div>
              <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-brand' : 'bg-gray-200'}`} />
              <div className={`flex items-center gap-1.5 ${step >= 2 ? 'text-brand' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${step >= 2 ? 'bg-brand text-white' : 'bg-gray-200'}`}>2</div>
                <span className="text-xs font-medium">Details</span>
              </div>
            </div>

            {/* Step 1 - Search */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-5 border border-gray-100">
                <h2 className="font-medium text-gray-900 mb-3">Find Your Venue</h2>
                <div className="flex gap-2 mb-4">
                  <input type="text" placeholder="Search by name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchVenues()} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="venue-search-input" />
                  <button onClick={searchVenues} disabled={searching} className="btn-primary text-sm px-4" data-testid="venue-search-btn">
                    {searching ? "..." : "Search"}
                  </button>
                </div>
                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    {searchResults.map((venue) => (
                      <div key={venue.id} onClick={() => { setSelectedVenue(venue); setStep(2); window.scrollTo(0, 0); }} className="p-3 rounded-lg border border-gray-200 hover:border-brand cursor-pointer flex items-center gap-3" data-testid={`venue-result-${venue.id}`}>
                        <img src={getVenueImage(venue, 200)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm text-gray-900 truncate">{venue.name}</h3>
                          <p className="text-xs text-gray-500">{venue.location.city}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searchQuery && searchResults.length === 0 && !searching && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-2">No unclaimed venues found</p>
                    <button 
                      onClick={() => setMode("add")} 
                      className="text-sm text-green-600 font-medium hover:text-green-700"
                    >
                      + Add your venue instead
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 2 - Owner Details */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-5 border border-gray-100">
                {selectedVenue && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                    <img src={getVenueImage(selectedVenue, 200)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    <div><h3 className="font-medium text-sm">{selectedVenue.name}</h3><p className="text-xs text-gray-500">{selectedVenue.location.city}</p></div>
                  </div>
                )}
                <h2 className="font-medium text-gray-900 mb-3">Your Details</h2>
                <form onSubmit={handleClaimSubmit} className="space-y-3">
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><User className="w-3.5 h-3.5" />Name *</label>
                    <input type="text" value={formData.owner_name} onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="John Smith" required data-testid="owner-name-input" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><Mail className="w-3.5 h-3.5" />Email *</label>
                    <input type="email" value={formData.owner_email} onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="john@venue.com" required data-testid="owner-email-input" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><Phone className="w-3.5 h-3.5" />Phone</label>
                    <input type="tel" value={formData.owner_phone} onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="+44 7700 900000" data-testid="owner-phone-input" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><Briefcase className="w-3.5 h-3.5" />Company</label>
                    <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Your Venue Ltd" data-testid="company-name-input" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full btn-primary" data-testid="submit-claim-btn">
                    {submitting ? "Claiming..." : "Claim Venue"}
                  </button>
                </form>
              </motion.div>
            )}
          </>
        )}

        {/* ADD VENUE MODE */}
        {mode === "add" && (
          <>
            {/* Steps for Add Mode */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className={`flex items-center gap-1.5 ${step >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${step >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>1</div>
                <span className="text-xs font-medium">Venue</span>
              </div>
              <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-green-600' : 'bg-gray-200'}`} />
              <div className={`flex items-center gap-1.5 ${step >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${step >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>2</div>
                <span className="text-xs font-medium">Your Details</span>
              </div>
            </div>

            {/* Step 1 - Venue Details */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-5 border border-gray-100">
                <h2 className="font-medium text-gray-900 mb-4">Venue Details</h2>
                
                <div className="space-y-4">
                  {/* Venue Name */}
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
                      <Building2 className="w-3.5 h-3.5" /> Venue Name *
                    </label>
                    <input
                      type="text"
                      value={venueData.name}
                      onChange={(e) => setVenueData({ ...venueData, name: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      placeholder="The Coffee House"
                      required
                      data-testid="venue-name-input"
                    />
                  </div>

                  {/* Venue Type */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-2 block">Venue Type *</label>
                    <div className="grid grid-cols-3 gap-2">
                      {venueTypes.map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setVenueData({ ...venueData, venue_type: value })}
                          className={`p-2.5 rounded-lg border text-xs font-medium flex flex-col items-center gap-1.5 transition-all ${
                            venueData.venue_type === value
                              ? "border-green-500 bg-green-50 text-green-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                          data-testid={`venue-type-${value}`}
                        >
                          <Icon className="w-4 h-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Description</label>
                    <textarea
                      value={venueData.description}
                      onChange={(e) => setVenueData({ ...venueData, description: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm h-20 resize-none"
                      placeholder="Tell remote workers what makes your venue great..."
                      data-testid="venue-description-input"
                    />
                  </div>

                  {/* Location */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                        <MapPin className="w-3.5 h-3.5" /> Location
                      </label>
                      <button
                        type="button"
                        onClick={getCurrentLocation}
                        disabled={gettingLocation}
                        className="text-xs text-green-600 font-medium flex items-center gap-1 hover:text-green-700"
                      >
                        <Locate className="w-3.5 h-3.5" />
                        {gettingLocation ? "Finding..." : "Use my location"}
                      </button>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={venueData.address}
                        onChange={(e) => setVenueData({ ...venueData, address: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        placeholder="Street address"
                        data-testid="venue-address-input"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={venueData.city}
                          onChange={(e) => setVenueData({ ...venueData, city: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          placeholder="City *"
                          required
                          data-testid="venue-city-input"
                        />
                        <input
                          type="text"
                          value={venueData.postcode}
                          onChange={(e) => setVenueData({ ...venueData, postcode: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          placeholder="Postcode"
                          data-testid="venue-postcode-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Amenities */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-2 block">Work-Friendly Amenities</label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                          <Wifi className="w-3.5 h-3.5" /> WiFi Quality
                        </label>
                        <select
                          value={venueData.wifi_quality}
                          onChange={(e) => setVenueData({ ...venueData, wifi_quality: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          data-testid="wifi-select"
                        >
                          {wifiOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                          <Volume2 className="w-3.5 h-3.5" /> Noise Level
                        </label>
                        <select
                          value={venueData.noise_level}
                          onChange={(e) => setVenueData({ ...venueData, noise_level: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          data-testid="noise-select"
                        >
                          {noiseOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                          <Plug className="w-3.5 h-3.5" /> Power Outlets
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={venueData.power_outlets}
                          onChange={(e) => setVenueData({ ...venueData, power_outlets: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          data-testid="power-input"
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 mb-1">
                          <Coffee className="w-3.5 h-3.5" /> Avg Coffee Price (£)
                        </label>
                        <input
                          type="number"
                          step="0.10"
                          min="0"
                          value={venueData.coffee_avg}
                          onChange={(e) => setVenueData({ ...venueData, coffee_avg: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                          data-testid="coffee-price-input"
                        />
                      </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {[
                        { key: "has_meeting_rooms", label: "Meeting Rooms", icon: Users },
                        { key: "has_parking", label: "Parking", icon: Car },
                        { key: "has_food", label: "Serves Food", icon: UtensilsCrossed },
                        { key: "has_drinks", label: "Serves Drinks", icon: GlassWater },
                      ].map(({ key, label, icon: Icon }) => (
                        <label
                          key={key}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                            venueData[key] ? "border-green-500 bg-green-50" : "border-gray-200"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={venueData[key]}
                            onChange={(e) => setVenueData({ ...venueData, [key]: e.target.checked })}
                            className="sr-only"
                          />
                          <Icon className={`w-4 h-4 ${venueData[key] ? "text-green-600" : "text-gray-400"}`} />
                          <span className={`text-xs ${venueData[key] ? "text-green-700 font-medium" : "text-gray-600"}`}>
                            {label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Opening Hours */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Opening Hours</label>
                    <input
                      type="text"
                      value={venueData.opening_hours}
                      onChange={(e) => setVenueData({ ...venueData, opening_hours: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      placeholder="e.g., Mon-Fri: 8am-6pm, Sat: 9am-5pm"
                      data-testid="hours-input"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => { setStep(2); window.scrollTo(0, 0); }}
                    disabled={!venueData.name || !venueData.city}
                    className="w-full btn-primary bg-green-600 hover:bg-green-700 disabled:bg-gray-300"
                    data-testid="next-step-btn"
                  >
                    Continue to Your Details
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2 - Owner Details (Add Mode) */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-5 border border-gray-100">
                {/* Venue Summary */}
                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-green-600" />
                    <h3 className="font-medium text-sm text-green-800">{venueData.name}</h3>
                  </div>
                  <p className="text-xs text-green-600">{venueData.city} • {venueTypes.find(t => t.value === venueData.venue_type)?.label}</p>
                </div>

                <h2 className="font-medium text-gray-900 mb-3">Your Details</h2>
                <form onSubmit={handleAddVenueSubmit} className="space-y-3">
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><User className="w-3.5 h-3.5" />Your Name *</label>
                    <input type="text" value={formData.owner_name} onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="John Smith" required data-testid="add-owner-name-input" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><Mail className="w-3.5 h-3.5" />Email *</label>
                    <input type="email" value={formData.owner_email} onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="john@venue.com" required data-testid="add-owner-email-input" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><Phone className="w-3.5 h-3.5" />Phone</label>
                    <input type="tel" value={formData.owner_phone} onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="+44 7700 900000" data-testid="add-owner-phone-input" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1"><Briefcase className="w-3.5 h-3.5" />Company / Business Name</label>
                    <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Your Venue Ltd" data-testid="add-company-name-input" />
                  </div>
                  <button type="submit" disabled={submitting} className="w-full btn-primary bg-green-600 hover:bg-green-700" data-testid="submit-add-venue-btn">
                    {submitting ? "Adding Venue..." : "Add My Venue"}
                  </button>
                </form>
              </motion.div>
            )}
          </>
        )}

        {/* Promo Code Display/Input */}
        {appliedPromo && promoValidated && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-green-800">Promo Code Applied!</p>
                <p className="text-sm text-green-600">
                  {promoDetails?.voucher_type === 'premium_trial' 
                    ? `🎉 ${promoDetails.value} days FREE premium - No commitment, no card required!`
                    : promoDetails?.description || appliedPromo}
                </p>
              </div>
              <code className="px-3 py-1 bg-green-100 text-green-800 rounded-lg font-mono text-sm font-bold">
                {appliedPromo}
              </code>
            </div>
          </div>
        )}

        {/* Benefits with Pricing */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="p-4 bg-white rounded-lg border border-gray-100 flex flex-col h-full">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Free</h3>
            <p className="text-lg font-bold text-gray-900 mb-3">
              £0<span className="text-xs font-normal text-gray-500">/forever</span>
            </p>
            <ul className="space-y-1.5 text-xs text-gray-600 flex-1">
              {(pricing?.free_features || ["Basic listing", "Up to 2 photos", "Appear in search"]).map((f) => (
                <li key={`free-${f}`} className="flex items-center gap-1.5">
                  <Check className="w-3 h-3 text-green-500 flex-shrink-0" />{f}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-gray-400 mt-3 pt-2 border-t border-gray-100">
              No credit card required
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200 flex flex-col h-full relative overflow-hidden">
            {promoValidated && promoDetails?.voucher_type === 'premium_trial' && (
              <div className="absolute top-0 right-0 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg">
                {promoDetails.value} DAYS FREE!
              </div>
            )}
            <div className="flex items-center gap-1 mb-1">
              <Crown className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-amber-800">Premium</h3>
            </div>
            <p className="text-lg font-bold text-amber-900 mb-3">
              £{pricing?.premium_annual || 149}<span className="text-xs font-normal">/year</span>
              <span className="block text-xs font-normal text-amber-600">or £{pricing?.premium_monthly || 15}/month</span>
            </p>
            <ul className="space-y-1.5 text-xs text-amber-700 flex-1">
              {(pricing?.premium_features || ["Featured placement", "Up to 5 photos", "Contact details", "Booking system", "Analytics"]).map((f) => (
                <li key={`premium-${f}`} className="flex items-center gap-1.5">
                  <Check className="w-3 h-3 text-amber-500 flex-shrink-0" />{f}
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-amber-600 mt-3 pt-2 border-t border-amber-200">
              {promoValidated && promoDetails?.voucher_type === 'premium_trial' 
                ? `✨ Your FREE month is ready to claim!`
                : "2 months free on annual. Cancel anytime."}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
